import { CardElement, useElements, useStripe } from "@stripe/react-stripe-js";
import moment from "moment";
import momentTz from 'moment-timezone';
import React, { useState } from "react";
import { Button, Row, Col, Form, Modal, Spinner, Alert } from "react-bootstrap";
import DatePicker from "react-datepicker";
import CardInput from "../../../Components/CardInput";
import { STRIPR_WARNING_MSG } from "../../../utils";
import { diplayCreditCard } from "../Cards/Utils";
import { ADD_SUBSCRIPTION_TYPES, required, requiredIf, TRANSACTION_MODES, paymentFieldsReqCallback } from "./AddSubscription";



const addPaymentValidators = [
    {
        key: "addSubscriptionType",
        validators: [required],
    },
    {
        key: "cardId",
        validators: [requiredIf(paymentFieldsReqCallback("cardId"))]
    },
    {
        key: "transactionId",
        validators: [requiredIf(paymentFieldsReqCallback("transactionId"))]
    },
    {
        key: "transactionMode",
        validators: [requiredIf(paymentFieldsReqCallback("transactionMode"))]
    },
    {
        key: "paymentDate",
        validators: [required]
    }
]

const getInitialPaymentDate = (subscription)=>{
    const subsTimeZone = subscription?.subscriptionType?.timezone;
    const paymentDate = momentTz.tz(subscription?.validityEndDate,subsTimeZone).add(1,'day');
    const year = paymentDate.year();
    const month = paymentDate.month();
    const date = paymentDate.date();
    return new Date(year, month, date);
}

const AddPayment = ({ show, handleClose, subscription, subscriptionTypes,  handleSuccess, cards, stripeKey }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [formData, setFormData] = useState({ values: { paymentDate: getInitialPaymentDate(subscription) }, errors: {} });
    const [submitting, setSubmitting] = useState(false);
    const [stripeError, setStripeError] = useState(undefined);
    const [apiError, setApiError] = useState(undefined);

    const selectedSubscriptionType = subscriptionTypes?.find(st => st.id === subscription?.subscriptionType?.id);
    const validateForm = (data)=>{
        const errors = {};
        addPaymentValidators.forEach(({ key, validators }) => {
            const value = data[key];
            let error;
            validators.forEach(validator => {
                if (!error) {
                    error = validator(value, data);
                }
            });
            if (error) {
                errors[key] = error;
            }
        });
        return errors;
    }
    const transactionValue = ((selectedSubscriptionType?.value *(100-selectedSubscriptionType?.discountPercentage))/100);

    const handleChange = (field, value) => {
        setFormData((prevFormData) => {
            const { values: prevValues, errors: prevErrors } = prevFormData;
            const validators = addPaymentValidators.find(({key})=>(key === field))?.validators || [];
            let error;
            validators.forEach(validator => {
                if (!error) {
                    error = validator(value);
                }
            });
            return {
                ...prevFormData,
                values: { ...prevValues, [field]: value },
                errors: { ...prevErrors, [field]: error}
            }
        });
    }

    const onSubmit = async () => {
        const { values: data } = formData;

        const errors = validateForm(data);

        if (Object.keys(errors).length > 0) {
            setFormData((prevFormData) => {
                const { errors: prevErrors } = prevFormData;
                return {
                    ...prevFormData,
                    errors: { ...prevErrors, ...errors }
                }
            })
            return;
        }

        const {addSubscriptionType} = data;
        let newCardToken;
        setSubmitting(true);
        setApiError(undefined);
        setStripeError(undefined);
        if(addSubscriptionType === "NEW_CARD"){
            if (!stripe || !elements) {
                // Stripe.js has not yet loaded.
                // Make sure to disable form submission until Stripe.js has loaded.
                setStripeError("Stripe hasn't loaded yet.");
                setSubmitting(false);
                return;
            }
            const { token:data, error }  = await stripe.createToken(elements.getElement(CardElement));
            if (error) {
                setStripeError(error.message);
                setSubmitting(false);
                return;
            }
            newCardToken = data.id;
        }

        const sanitizedData = {
            userSubscriptionId: subscription?.id,
            paymentType: data?.addSubscriptionType,
            cardId: data?.cardId,
            newCardToken,
            transactionId: data?.transactionId,
            transactionMode: data?.transactionMode,
            transactionValue: transactionValue,
            transactionCurrencyCode: selectedSubscriptionType?.currencyCode,
            paymentDate: moment(data?.paymentDate).format("YYYY-MM-DD"),
            notes: data?.notes,
            assignLoyaltyPoints: !!data?.assignLoyaltyPoints,
            saveCard: !!data?.saveCreditCard
            
        };

        try {
            await handleSuccess(sanitizedData);
            setSubmitting(false);
        } catch (e) {
            setApiError(e?.message || "System error");
            setSubmitting(false);
            console.log(e)
        }

    }
    const addSubscriptionType = formData?.values?.addSubscriptionType;
    const noExistingCards = !(cards?.length !== 0);
    const {validityDays} = selectedSubscriptionType;
    const nextDillingDate = moment(formData?.values?.paymentDate).add('days',(validityDays)).format("DD/MM/YYYY");
    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title> Add Payment for {subscription?.subscriptionType?.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form >
                    <Form.Group as={Row} className="mb-3" controlId="frmAddSubscriptionType">
                        <Form.Label column sm={3}>Payment Method*</Form.Label>
                        <Col sm={9}>
                            <Form.Select
                                value={addSubscriptionType}
                                onChange={(e) => handleChange('addSubscriptionType', e.target.value)}
                                isInvalid={formData?.errors?.addSubscriptionType}
                            >
                                <option value="">--</option>
                                {ADD_SUBSCRIPTION_TYPES.map(st => {
                                    const isExistingOpt = st.value === "EXISTING_CARD";
                                    const isDisabled = isExistingOpt && noExistingCards;
                                    return (<option
                                        key={st.value}
                                        value={st.value}
                                        disabled={isDisabled}
                                    >
                                        {`${st.label}${isDisabled ? ' (No cards found)': ''}`}
                                    </option>)
                                })}
                            </Form.Select>
                            {formData?.errors?.addSubscriptionType && (<span className="error-text">
                                {formData?.errors?.addSubscriptionType}
                            </span>)}
                        </Col>
                    </Form.Group>
                    {(addSubscriptionType === "EXISTING_CARD")&& (
                         <Form.Group as={Row} className="mb-3" controlId="frmCardId">
                         <Form.Label column sm={3}>Credit Card*</Form.Label>
                         <Col sm={9}>
                             <Form.Select
                                 value={formData?.values?.cardId}
                                 onChange={(e) => handleChange('cardId', e.target.value)}
                                 isInvalid={formData?.errors?.cardId}
                             >
                                 <option value="">--</option>
                                 {cards.map(card => (<option key={card.id} value={card.id}>{diplayCreditCard(card)}</option>))}
                             </Form.Select>
                             {formData?.errors?.cardId && (<span className="error-text">
                                 {formData?.errors?.cardId}
                             </span>)}
                         </Col>
                     </Form.Group>
                    )}
                    {(addSubscriptionType === "NEW_CARD")&& (
                        <>
                            <Form.Group as={Row} className="mb-3" controlId="frmNewCardToken">
                                {(!stripeKey) && (
                                    <Alert variant="warning">{STRIPR_WARNING_MSG}</Alert>
                                )}
                                <Form.Label column sm={3}>New Credit Card*</Form.Label>
                                <Col sm={9}>
                                    <div className="border-box">
                                        <CardInput/>
                                    </div>
                                    {stripeError && (<span className="error-text">
                                        {stripeError}
                                    </span>)}
                                </Col>
                            </Form.Group>
                            <Form.Group as={Row} className="mb-3" controlId="frmSaveCreditCard">
                                <Col>
                                    <Form.Check
                                        type="checkbox" label="Save credit card"
                                        value={!!formData?.values?.saveCreditCard}
                                        onChange={(e) => handleChange('saveCreditCard', !formData?.values?.saveCreditCard)}
                                    />
                                </Col>
                            </Form.Group>
                        </>
                    )}
                    {(addSubscriptionType === "MANUAL") && (<>
                        <Form.Group as={Row} className="mb-3" controlId="frmTransactionId">
                            <Form.Label column sm={3}>
                                Transaction ID*
                            </Form.Label>
                            <Col sm={9}>
                                <Form.Control
                                    type="text" placeholder=""
                                    value={formData?.values?.transactionId || ''}
                                    onChange={(e) => handleChange('transactionId', e.target.value)}
                                    isInvalid={formData?.errors?.transactionId}
                                />
                                {formData?.errors?.transactionId && (<span className="error-text">
                                    {formData?.errors?.transactionId}
                                </span>)}
                            </Col>
                        </Form.Group>
                        <Form.Group as={Row} className="mb-3" controlId="frmTransactionMode">
                            <Form.Label column sm={3}>Transaction Mode*</Form.Label>
                            <Col sm={9}>
                                <Form.Select
                                    value={formData?.values?.transactionMode}
                                    onChange={(e) => handleChange('transactionMode', e.target.value)}
                                    isInvalid={formData?.errors?.transactionMode}
                                >
                                    <option value="">--</option>
                                    {TRANSACTION_MODES.map(st => (<option key={st.value} value={st.value}>{st.label}</option>))}
                                </Form.Select>
                                {formData?.errors?.transactionMode && (<span className="error-text">
                                    {formData?.errors?.transactionMode}
                                </span>)}
                            </Col>
                        </Form.Group>
                    </>)}
                    <Form.Group as={Row} className="mb-3" controlId="frmPaymentDate">
                        <Col sm={3}>
                            <Form.Label>Payment Date*</Form.Label>
                            <div className="d-flex flex-column">
                                <span style={{fontSize : 12}}>Note: The credit card will be charged instantly.</span>
                                <span style={{fontSize : 12}}>{`Next subscription cycle will be ${validityDays} days from this date.`}</span>
                            </div>
                        </Col>
                        <Col sm={9} style={{position:'relative'}}>
                                <DatePicker
                                    dateFormat="dd/MM/yyyy"
                                    selected={formData?.values?.paymentDate}
                                    onChange={(date) => handleChange('paymentDate', date)}
                                />
                                <div style={{
                                    position: 'absolute',
                                    top : 6,
                                    right : 30,
                                    display : 'flex',
                                    alignItems : 'center'
                                }}>
                                    <span
                                        style={{
                                            fontSize : 12,
                                            marginRight : 15
                                        }}
                                    >Next billing date will be set to </span>
                                    <span>{nextDillingDate}</span>
                                </div>
                            
                             {formData?.errors?.paymentDate && (<span className="error-text">
                                {formData?.errors?.paymentDate}
                            </span>)}
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="mb-3" controlId="frmNotes">
                        <Form.Label column sm={3}>
                            Notes
                        </Form.Label>
                        <Col sm={9}>
                            <Form.Control
                                type="text" as={'textarea'} rows={2} placeholder=""
                                value={formData?.values?.notes || ''}
                                onChange={(e) => handleChange('notes', e.target.value)}
                                isInvalid={formData?.errors?.notes}
                            />
                            {formData?.errors?.notes && (<span className="error-text">
                                {formData?.errors?.notes}
                            </span>)}
                        </Col>
                    </Form.Group>
                    <Form.Group as={Row} className="mb-3" controlId="frmAssignLoyaltyPoints">
                        <Col>
                            <Form.Check
                                type="checkbox" label="Assign loyalty points"
                                value={!!formData?.values?.assignLoyaltyPoints}
                                onChange={(e) => handleChange('assignLoyaltyPoints', !formData?.values?.assignLoyaltyPoints)}
                            />
                        </Col>
                    </Form.Group>
                    {selectedSubscriptionType && <Form.Group as={Row} className="mb-3">
                        <h4>Total : {transactionValue} {selectedSubscriptionType?.currencyCode}</h4>
                    </Form.Group>}
                </Form>
                {apiError && (
                    <Alert variant="danger">{apiError}</Alert>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Close
                </Button>
                <Button variant="primary" onClick={onSubmit} disabled={submitting}>
                    {submitting && <Spinner
                        as="span"
                        animation="grow"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                    />}
                    Add Payment
                </Button>
            </Modal.Footer>
        </Modal>
    )
}

export default AddPayment;